import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Rocket, Ribbon, Tag, User, Mail, Lock, ArrowLeft, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";
import { AppleSignInButton } from "@/components/AppleSignInButton";

export default function Signup() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { register } = useAuth();
  const [invite, setInvite] = useState("");
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [founding, setFounding] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [unameStatus, setUnameStatus] = useState("idle"); // idle | checking | ok | taken | invalid

  useEffect(() => {
    const u = username.trim();
    if (!u) { setUnameStatus("idle"); return; }
    if (!/^[a-z0-9_.]{3,20}$/.test(u.toLowerCase())) { setUnameStatus("invalid"); return; }
    setUnameStatus("checking");
    let active = true;
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/auth/username-available?username=${encodeURIComponent(u)}`);
        if (active) setUnameStatus(r.available ? "ok" : (r.reason === "invalid" ? "invalid" : "taken"));
      } catch { if (active) setUnameStatus("idle"); }
    }, 450);
    return () => { active = false; clearTimeout(t); };
  }, [username]);

  useEffect(() => {
    const code = params.get("code") || params.get("invite");
    if (code) { setInvite(code.trim().toUpperCase()); setShowInvite(true); }
    api.get("/public/founding").then(setFounding).catch(() => {});
  }, [params]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim() || !email.trim() || password.length < 6) return setError("Fill all fields (password min 6 chars)");
    if (username.trim() && !/^[a-z0-9_.]{3,20}$/.test(username.trim().toLowerCase())) return setError("Username must be 3-20 characters: letters, numbers, dots or underscores");
    if (!agreed) return setError("Please agree to the Terms of Service and Privacy Policy to continue");
    setLoading(true);
    try {
      await register(name.trim(), email.trim(), password, invite.trim(), username.trim());
      navigate("/");
    } catch (err) {
      setError(err.message || "Sign up failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-wrap">
      <div className="auth-card fade-up">
        <button className="ss-nav-item" style={{ width: "auto", marginBottom: 10, marginLeft: -8 }} onClick={() => navigate("/landing")} data-testid="signup-back-button">
          <ArrowLeft size={20} />
        </button>
        <div className="ss-pill" style={{ color: "var(--gold)", borderColor: "var(--gold)" }}>
          <Rocket size={12} /> FREE TO JOIN
        </div>

        <>
            <h1 style={{ fontSize: 30, marginTop: 14 }}>Join SouqStream</h1>
            <p className="ss-muted" style={{ marginTop: 6 }}>Create your free account — it only takes a few seconds.</p>
            {founding && founding.remaining > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, background: "var(--gold-soft)", border: "1px solid var(--gold)", borderRadius: 12, padding: "10px 12px", marginTop: 14, fontSize: 13, fontWeight: 600 }} data-testid="signup-founding-note">
                <Ribbon size={15} style={{ color: "var(--gold-2)" }} />
                First {founding.limit.toLocaleString()} members get the <b>Founding Member</b> badge — {founding.remaining.toLocaleString()} spots left
              </div>
            )}
            <form style={{ marginTop: 22, display: "flex", flexDirection: "column", gap: 14 }} onSubmit={onSubmit}>
              <div className="ss-field"><User size={18} className="ss-muted" /><input placeholder="Display name" value={name} onChange={(e) => setName(e.target.value)} data-testid="signup-name-input" /></div>
              <div>
                <div className="ss-field" style={{ borderColor: unameStatus === "ok" ? "var(--brand)" : unameStatus === "taken" || unameStatus === "invalid" ? "var(--red)" : undefined }}>
                  <span className="ss-muted" style={{ fontWeight: 800 }}>@</span>
                  <input placeholder="Username (optional)" value={username} onChange={(e) => setUsername(e.target.value.replace(/^@/, ""))} data-testid="signup-username-input" />
                  {unameStatus === "checking" && <Loader2 size={16} className="ss-muted" style={{ animation: "spin 1s linear infinite" }} data-testid="username-checking" />}
                  {unameStatus === "ok" && <CheckCircle2 size={16} style={{ color: "var(--brand)" }} data-testid="username-available" />}
                  {(unameStatus === "taken" || unameStatus === "invalid") && <XCircle size={16} style={{ color: "var(--red)" }} data-testid="username-taken" />}
                </div>
                <div className="ss-muted" style={{ fontSize: 12, marginTop: 4, color: unameStatus === "ok" ? "var(--brand)" : (unameStatus === "taken" || unameStatus === "invalid") ? "var(--red)" : undefined }}>
                  {unameStatus === "ok" && "✓ Available"}
                  {unameStatus === "taken" && "✗ That username is taken"}
                  {unameStatus === "invalid" && "3-20 chars: letters, numbers, dots or underscores"}
                  {(unameStatus === "idle" || unameStatus === "checking") && "Pick a @username, or leave blank and we'll create one for you."}
                </div>
              </div>
              <div className="ss-field"><Mail size={18} className="ss-muted" /><input placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="signup-email-input" /></div>
              <div className="ss-field"><Lock size={18} className="ss-muted" /><input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="signup-password-input" /></div>
              {showInvite ? (
                <div className="ss-field">
                  <Tag size={18} className="ss-muted" />
                  <input placeholder="Invite code (optional)" value={invite} onChange={(e) => setInvite(e.target.value.toUpperCase())} style={{ fontWeight: 700, letterSpacing: 1 }} data-testid="signup-invite-input" />
                </div>
              ) : (
                <div className="ss-muted" style={{ fontSize: 13 }}>Have a referral code? <span style={{ color: "var(--brand)", fontWeight: 800, cursor: "pointer" }} onClick={() => setShowInvite(true)} data-testid="add-invite-code">Add it</span></div>
              )}
              <label style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 13, lineHeight: 1.5, cursor: "pointer", color: "var(--muted)" }} data-testid="signup-consent-label">
                <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} style={{ marginTop: 2, width: 16, height: 16, accentColor: "var(--brand)", flexShrink: 0, cursor: "pointer" }} data-testid="signup-consent-checkbox" />
                <span>I agree to the{" "}
                  <a href="/terms" target="_blank" rel="noreferrer" style={{ color: "var(--brand)", fontWeight: 700 }} data-testid="signup-terms-link">Terms of Service</a>{" "}and{" "}
                  <a href="/privacy" target="_blank" rel="noreferrer" style={{ color: "var(--brand)", fontWeight: 700 }} data-testid="signup-privacy-link">Privacy Policy</a>.
                </span>
              </label>
              {error && <div style={{ color: "var(--red)", fontWeight: 600 }} data-testid="signup-error">{error}</div>}
              <button className="ss-btn ss-btn-primary ss-btn-block" disabled={loading || !agreed || unameStatus === "taken" || unameStatus === "invalid"} data-testid="signup-submit-button">{loading ? "Creating…" : "Create Account"}</button>
            </form>
        </>

        <GoogleSignInButton redirectTo="/" referralCode={invite} />
        <AppleSignInButton redirectTo="/" referralCode={invite} />

        <div style={{ textAlign: "center", marginTop: 20 }} className="ss-muted">
          Already have an account? <span style={{ color: "var(--brand)", fontWeight: 800, cursor: "pointer" }} onClick={() => navigate("/login")} data-testid="go-login-button">Log in</span>
        </div>
      </div>
    </div>
  );
}
